/**
 * The Deal Discovery module's fixed strings.
 *
 * Same discipline as `provenance.ts` and for a stronger reason. `DEAL_DISCOVERY_COPY §0`: in
 * several regimes *advertising* that you act as an agent is an offence in itself, independent of
 * whether you ever do — so on these surfaces the wording is the control, not decoration. Every
 * string here is quoted from `docs/03_DESIGN/DEAL_DISCOVERY_COPY.md`, and
 * `deal-discovery.test.ts` parses that document and fails if a character drifts.
 *
 * They are also the reason the banned-word checker has an exception mechanism at all: a note has to
 * be able to say it is "not advice" and "not a recommendation", and a model writing prose must not.
 */

/**
 * `DEAL_DISCOVERY_COPY §2` — the header, subhead and body of the daily note's own page.
 *
 * Revised 13 Sep 2026 by the founder's instruction, carried in the design delivery
 * (`exports/v0.1/strings.json public_research`, marked "revised by client instruction"): the message
 * is that we analysed the business and there is value that could be released by automating or
 * improving it — not that we took it apart. The earlier wording is retired, not kept as an alias.
 */
export const NOTE_HEADER = "One business a day, analysed in public.";

export const NOTE_SUBHEAD =
  "Every weekday we pick a business that's currently advertised for sale and analyse how it actually "
  + "runs — read the numbers, check the claims, find what the advertisement leaves out, and work out "
  + "how much value could be released by automating or improving it. Then we publish the lot. Free. "
  + "No mandate, no commission, no side.";

export const NOTE_BODY =
  "Most businesses for sale are described by someone paid to sell them. We're not that. We read the "
  + "public record, speak to the owner where they'll talk to us, and write what we find — including "
  + "when the answer is that the asking price doesn't hold up.";

/** The section heading over the method, on the site. */
export const NOTE_SECTION_HEADING = "How we analyse one.";

/**
 * What each note covers, in order. The fourth line names the currency unit in words — "a dollar"
 * in `au` and `ca`, "a pound" in `uk` — so it is a function of the market's `spend_unit`.
 */
export function noteCovers(spendUnit: string): readonly string[] {
  return [
    "What the business actually does, and who it depends on",
    "The numbers as published, and what's missing from them",
    "Structure — asset sale, share sale, or share sale with property — and why that changes everything",
    `What we'd want to ask before spending ${spendUnit}`,
    "How much value could be released by automating or improving how it runs",
  ];
}
export const NOTE_COVERS_TEMPLATE = noteCovers("{{ spend_unit }}");

/** Section five's heading and lead — the range is our working, not a finding. */
export const NOTE_UPSIDE_HEADING = "Value that could be released a year, by automating or improving how it runs";
export const NOTE_UPSIDE_LEAD =
  "We analysed how it runs. This is what could be released by automating the office work and improving "
  + "the parts that depend on one person.";
export const NOTE_UPSIDE_CONFIDENCE =
  "A range, from public information and one conversation. It is our working, not a finding, and nothing "
  + "here has been checked against the business's records.";

/**
 * The status badge's five words, keyed by `liveness_status`. "Advertised", not "listed": the
 * banned list is phrase-based and "listing" is still the word the code uses, but a reader of a public
 * page should see the plain word, and it is the one the founder chose.
 */
export const NOTE_STATUS_LABEL = {
  live: "Still advertised",
  withdrawn: "Withdrawn",
  under_offer: "Under offer",
  sold: "Sold",
  relisted: "Re-advertised at a lower price",
  unknown: "Status not confirmed",
} as const;
export type NoteStatus = keyof typeof NOTE_STATUS_LABEL;

/**
 * `DEAL_DISCOVERY_COPY §2` — the footer under every research note.
 *
 * Three claims, and all three are load-bearing: independent research from public information, no
 * engagement with any party to the sale, and — where we speak to a seller — that we do it as
 * researchers, in our own name, and say so.
 */
export const NOTE_FOOTER =
  "These notes are independent research based on publicly available information and, where offered, "
  + "information supplied by the seller. They are not advice, not an offer, not a recommendation, and "
  + "we have no engagement with any party to any of these sales. Where we speak to a seller, we do so "
  + "as researchers, in our own name, and we say so.";

/**
 * What section five says when the engine can describe the business but cannot value it (DQ-75).
 *
 * Deliberately not `NOT_PRICEABLE_YET`, which is the same first sentence addressed to a paying
 * client and promises to come back to them. A reader of a public note is not owed a follow-up and
 * must not be told they are.
 */
export const NOTE_NOT_PRICEABLE =
  "We can see where the hours go, but we don't yet have the figures for this kind of business to put "
  + "a range on what they're worth.";

/**
 * What section five says in a market whose benchmark tables do not exist yet (GA-90).
 *
 * The alternative was to leave the section out, and leaving it out would read as though there were
 * nothing to say. There is: the reason is ours and the note says so.
 */
export const NOTE_MARKET_NOT_COVERED =
  "Our figures cover Australian businesses today. We've written up everything else; the range will "
  + "follow when the tables for this market exist.";

/** What section five says when the listing carries too little to price anything at all. */
export const NOTE_INSUFFICIENT_INPUT =
  "The listing doesn't say enough to put a range on this one. What we'd need is in the open questions.";

/** What section five says when no valuation run has produced a defensible range. */
export const NOTE_NO_RANGE =
  "No value range is published from public information alone. A range appears only when the inputs are sufficient and traceable.";

/** `DEAL_DISCOVERY_COPY §3` — the Layer 2 page's header, subhead and the load-bearing positioning paragraph. */
export const DILIGENCE_HEADER = "You've found one. Now find out what's actually there.";
export const DILIGENCE_SUBHEAD =
  "Fixed fee. We do the investigation — including the questions the seller would rather you didn't ask — "
  + "and hand you a file. You decide. You negotiate. You own the relationship.";
export const DILIGENCE_POSITIONING =
  "This is due diligence, the same thing a corporate finance team does on a large acquisition, built to "
  + "run on deals that were never big enough to afford one. We investigate and we report. We do not act "
  + "for you in the transaction, we do not carry offers or prices between you and the seller, and our fee "
  + "is the same whether you buy or walk away.";
export const DILIGENCE_TRANSPARENCY_HEADER = "Every call recorded. Every transcript yours.";
export const DILIGENCE_TRANSPARENCY =
  "When we contact a seller for information, we say who we are and that you're the one asking. You get "
  + "the recording and the transcript, not our summary of it.";
export const DILIGENCE_HANDOFF = "Then take it to your solicitor. They'll bill you for judgment instead of for chasing documents.";

/** `DEAL_DISCOVERY_COPY §4` — the Layer 3 landing header and subhead, and the engagement terms. */
export const VALUE_CREATION_HEADER = "Your business didn't sell. That's usually fixable.";
export const VALUE_CREATION_SUBHEAD =
  "Businesses sit on the market because the buyer can't see what they're getting, or can't see it running "
  + "without you. Both are engineering problems.";
export const VALUE_CREATION_TERMS: readonly string[] = [
  "Retainer for the work, monthly",
  "equity for the upside — a stake, like any other shareholder",
  "no commission; we take nothing from your sale",
  "typical engagement 9–18 months",
];

/**
 * `DEAL_DISCOVERY_COPY §4a` — the referral arrangement with brokers, decided 13 Sep 2026: a flat fee
 * per referred client, never a share of a sale. A share would be a contingent success fee, which
 * `jurisdiction.yaml prohibited_fee_models_everywhere` forbids platform-wide.
 */
export const REFERRAL_TERMS_FLAT_FEE =
  "A flat fee per referred client — the same amount whoever they are and whatever happens next. Never a "
  + "share of a sale, never a percentage, and never contingent on a transaction completing. Agreed in "
  + "writing before the first report goes out.";

/** `DEAL_DISCOVERY_COPY §6` — what a gated market shows where Layer 2 and Layer 3 are off. */
export const LAYER_GATED_NOTICE =
  "We publish research in Australia, Canada and the UK. Paid diligence is available only where the "
  + "regional service is enabled. Leave your email "
  + "and we'll tell you when it opens here.";

/** `DEAL_DISCOVERY_COPY §3` — the footer under every Layer 2 surface. */
export const DILIGENCE_FOOTER =
  "We are a research and diligence provider. We are not your agent, we do not act for you in any "
  + "transaction, we do not negotiate, and we do not convey offers or terms between parties. Our fee "
  + "is fixed and is not contingent on any transaction completing. You should take independent legal "
  + "and accounting advice before entering any agreement.";

/** `DEAL_DISCOVERY_COPY §4` — the footer under every Layer 3 surface. */
export const VALUE_CREATION_FOOTER =
  "We are an operational consultancy. We do not sell businesses, act as agent for any party to a "
  + "sale, introduce buyers or investors, or receive any fee contingent on a sale. Our compensation "
  + "is a retainer and an equity interest. Any eventual disposal is yours to run with advisers of "
  + "your choosing.";

/** `DEAL_DISCOVERY_COPY §1` — present on every Layer 2 and Layer 3 page. */
export const NOT_YOUR_AGENT = "We are not your agent. We do not negotiate on your behalf.";

/** `DEAL_DISCOVERY_COPY §1` — the independent-advice line. */
export const INDEPENDENT_ADVICE =
  "Engage your own solicitor and accountant. We make their job smaller, not optional.";

/**
 * `DEAL_DISCOVERY_COPY §5` — the on-site disclosure.
 *
 * The founder's draft said "produced by AI systems"; `ai-powered` is a banned word and the
 * Constitution's approved provenance wording is "prepared by our systems", so this uses that.
 */
export const AI_DISCLOSURE_ON_SITE =
  "Research notes on this site are produced by our systems with human review before publication. "
  + "When we contact a seller, we identify ourselves as researchers, explain the purpose, and provide "
  + "a way to reach a person.";

/** `DEAL_DISCOVERY_COPY §5` — the disclosure on the intake page itself (ai_disclosure_required is true in every region). */
export const AI_DISCLOSURE_INTAKE =
  "Our assistant is automated and says so. A person on our team reads everything it writes down, and you "
  + "can ask for a person at any point.";

/** `DEAL_DISCOVERY_COPY §5` — the footer on an automated outbound email, pre-client. */
export const AI_DISCLOSURE_EMAIL_RESEARCH =
  "This message was prepared using Operator Capital's automated research assistant and sent by us as "
  + "researchers. Reply to reach a person.";

/** The reviewed public-source brief sent to a verified owner or appointed adviser. */
export const OWNER_BRIEF_SUBJECT = "A business with a future worth testing — our initial report";
export const OWNER_BRIEF_OPENING =
  "We believe this is a business with a future worth testing. Our initial public-source report sets out "
  + "what looks promising, what remains unverified, and the practical systems that may make it more valuable.";
export const OWNER_BRIEF_OFFER =
  "If you would like a deeper report, reply with the supporting records. We can turn the public review into "
  + "a detailed operating-value and automation plan, and share the reviewed findings with you.";

/** Every fixed string in this file, so the banned-word checker's exception list cannot drift. */
export const DEAL_DISCOVERY_STRINGS: readonly string[] = [
  NOTE_HEADER, NOTE_SUBHEAD, NOTE_BODY, NOTE_SECTION_HEADING, ...NOTE_COVERS_TEMPLATE,
  NOTE_UPSIDE_HEADING, NOTE_UPSIDE_LEAD, NOTE_UPSIDE_CONFIDENCE, ...Object.values(NOTE_STATUS_LABEL),
  NOTE_FOOTER, NOTE_NOT_PRICEABLE, NOTE_MARKET_NOT_COVERED, NOTE_INSUFFICIENT_INPUT, NOTE_NO_RANGE,
  DILIGENCE_HEADER, DILIGENCE_SUBHEAD, DILIGENCE_POSITIONING, DILIGENCE_TRANSPARENCY_HEADER, DILIGENCE_TRANSPARENCY, DILIGENCE_HANDOFF,
  VALUE_CREATION_HEADER, VALUE_CREATION_SUBHEAD, ...VALUE_CREATION_TERMS, REFERRAL_TERMS_FLAT_FEE,
  LAYER_GATED_NOTICE, DILIGENCE_FOOTER, VALUE_CREATION_FOOTER, NOT_YOUR_AGENT, INDEPENDENT_ADVICE,
  AI_DISCLOSURE_ON_SITE, AI_DISCLOSURE_INTAKE, AI_DISCLOSURE_EMAIL_RESEARCH,
  OWNER_BRIEF_SUBJECT, OWNER_BRIEF_OPENING, OWNER_BRIEF_OFFER,
];
